import { Fragment, useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { gql, useMutation, useQuery } from "@apollo/client"
import { Banner } from "../../util/Banner"
import { useParams } from "react-router-dom"
import { ProgressBar } from "../../util/ProgressBar"
import tableStyle from "../../../style/table.module.css"
import style from "./EmailExportDetail.module.css"
import cn from "classnames"
import buttonStyle from "../../../style/button.module.css"
import { useDispatch } from "react-redux"
import { showError, showInfo } from "../../../redux/actions/NotificationActions"
import c from "capitalize"
import { Loader } from "../../util/Loader"
import Markdown from 'react-markdown'

const GET_EMAIL_EXPORT = gql`
    query emailExport($id: UUID!) {
        emailExport(id: $id) {
            id
            createdAt
            updatedAt
            totalPricingLists
            finishedPricingLists
            distributors {
                id
                name
                emails(limit: -1) {
                    page {
                        id
                        email
                    }
                }
            }
            user {
                id
                name
            }
            pricingListCalculations {
                id
                isDone
                hasError
                fileName
                result
                virtualTypeNumber
                distributor {
                    id
                    name
                }
                pricingListCalculation {
                    id
                    pricingList {
                        id
                        name
                        pricingListType {
                            id
                            displayName
                            pricingListTypeNumber
                        }
                    }
                }
            }
        }
    }
`

const GET_RENDERED_EMAIL = gql`
    query getRenderedEmail($distributorId: UUID!, $pricingListCalculationId: UUID!, $virtualTypeNumber: String) {
        distributor(id: $distributorId) {
            id
            renderedEmailTitle(pricingListCalculationId: $pricingListCalculationId, virtualTypeNumber: $virtualTypeNumber)
            renderedEmailMessage(pricingListCalculationId: $pricingListCalculationId, virtualTypeNumber: $virtualTypeNumber)
        }
    }
`

const SEND_EMAIL_EXPORT = gql`
    mutation sendEmailExport($emailExportId: UUID!) {
        sendEmailExport(emailExportId: $emailExportId)
    }
`

export const EmailExportDetail = () => {
    const { id } = useParams()
    const { t } = useTranslation(["common", "table"])

    const { data, loading, error, startPolling, stopPolling } = useQuery(GET_EMAIL_EXPORT, {
        variables: { id },
    })

    const exportData = data?.emailExport
    const isComplete = exportData?.finishedPricingLists === exportData?.totalPricingLists
    const progress = exportData?.totalPricingLists > 0
        ? exportData.finishedPricingLists / exportData.totalPricingLists
        : 0

    // Start/stop polling based on completion status
    useEffect(() => {
        if (isComplete) {
            stopPolling()
        } else {
            startPolling(30000) // Poll every 30 seconds
        }

        return () => stopPolling()
    }, [isComplete, startPolling, stopPolling])

    if (loading && !data) {
        return (
            <>
                <Banner text={t("table:email_export_detail")} />
                <Loader />
            </>
        )
    }

    if (error) {
        return (
            <div>
                <Banner text={t("table:email_export_detail")} />
                <div style={{ padding: "2rem" }}>
                    <p>{t("common:error")}: {error.message}</p>
                </div>
            </div>
        )
    }

    // Show proofing view when complete
    if (isComplete) {
        return <ProofingView exportData={exportData} id={id} />
    }

    // Show progress view while processing
    return (
        <div>
            <Banner text={t("table:email_export_detail")} />

            <div className={style.infoBar}>
                <div className={style.infoItem}>
                    <strong>{t("table:distributor")}:</strong> {exportData?.distributors?.map(d => d.name).join(", ") || "-"}
                </div>
                <div className={style.infoItem}>
                    <strong>{t("table:exporter")}:</strong> {exportData?.user?.name || "-"}
                </div>
                <div className={style.infoItem}>
                    <strong>{t("table:created_at")}:</strong> {exportData?.createdAt ? new Date(exportData.createdAt).toLocaleString() : "-"}
                </div>
            </div>

            <div className={style.progressSection}>
                <ProgressBar progress={progress}>
                    {exportData?.finishedPricingLists} / {exportData?.totalPricingLists} {t("table:pricing_lists")}
                </ProgressBar>
            </div>

            <div
                className={tableStyle.table}
                style={{ gridTemplateColumns: "2fr 1fr 2fr 1fr 1fr" }}
            >
                <p className={tableStyle.header}>{t("table:pricing_list")}</p>
                <p className={tableStyle.header}>{t("table:type")}</p>
                <p className={tableStyle.header}>{t("table:distributor")}</p>
                <p className={tableStyle.header}>{t("table:file_name")}</p>
                <p className={tableStyle.header}>{t("table:status")}</p>

                {exportData?.pricingListCalculations?.map((calc, index) => (
                    <Fragment key={calc.id || index}>
                        <p className={tableStyle.cell}>
                            {calc.pricingListCalculation?.pricingList?.name || "-"}
                        </p>
                        <p className={tableStyle.cell}>
                            {calc.pricingListCalculation?.pricingList?.pricingListType?.displayName || "-"}
                        </p>
                        <p className={tableStyle.cell}>
                            {calc.distributor?.name || "-"}
                        </p>
                        <p className={tableStyle.cell}>
                            {calc.fileName || "-"}
                        </p>
                        <p className={cn(tableStyle.cell, tableStyle.lastCell)}>
                            {calc.hasError ? (
                                <span style={{ color: "var(--red)" }}>✗ {t("common:error")}</span>
                            ) : calc.isDone ? (
                                <span style={{ color: "var(--green)" }}>✓ {t("common:finish")}</span>
                            ) : (
                                <span style={{ color: "var(--orange)" }}>⟳ {t("common:loading")}</span>
                            )}
                        </p>
                    </Fragment>
                ))}
            </div>
        </div>
    )
}

const ProofingView = ({ exportData, id }) => {
    const { t } = useTranslation(["common", "table"])
    const dispatch = useDispatch()
    const [activeTab, setActiveTab] = useState(0)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const textareaRef = useRef(null)

    const [sendEmailExport, { loading: sendingEmail }] = useMutation(SEND_EMAIL_EXPORT)

    const calculations = exportData?.pricingListCalculations || []
    const activeCalc = calculations[activeTab]

    const downloadDatFile = (calc) => {
        const blob = new Blob([calc.result], { type: 'text/plain' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = calc.fileName
        a.click()
        URL.revokeObjectURL(url)
    }

    const jumpToTop = () => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop = 0
        }
    }

    const jumpToBottom = () => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight
        }
    }

    const handleSendEmail = () => {
        sendEmailExport({ variables: { emailExportId: id } })
            .then(() => {
                dispatch(showInfo("common:export_triggered"))
                setShowConfirmModal(false)
            })
            .catch((e) => dispatch(showError(e.message)))
    }

    return (
        <div>
            <Banner text={t("table:email_export_detail")} />

            <div className={style.infoBar}>
                <div className={style.infoItem}>
                    <strong>{t("table:distributor")}:</strong> {exportData?.distributors?.map(d => d.name).join(", ") || "-"}
                </div>
                <div className={style.infoItem}>
                    <strong>{t("table:exporter")}:</strong> {exportData?.user?.name || "-"}
                </div>
                <div className={style.infoItem}>
                    <strong>{t("table:created_at")}:</strong> {exportData?.createdAt ? new Date(exportData.createdAt).toLocaleString() : "-"}
                </div>
                <div style={{ marginLeft: "auto" }}>
                    <button
                        className={buttonStyle.button}
                        onClick={() => setShowConfirmModal(true)}
                        disabled={sendingEmail}
                    >
                        {t("table:send_email")}
                    </button>
                </div>
            </div>

            {/* Tab Navigation - Organized by pricing list type, showing distributor names for duplicates */}
            <div className={style.tabBar}>
                {calculations.map((calc, index) => {
                    // Use virtual type number if available, otherwise fall back to pricing list name
                    const pricingListName = calc.virtualTypeNumber
                        || calc.pricingListCalculation?.pricingList?.name
                        || `List ${index + 1}`
                    const distributorName = calc.distributor?.name || ""

                    // Count how many times this name/number appears across all calculations
                    const duplicateCount = calculations.filter(c => {
                        const otherName = c.virtualTypeNumber
                            || c.pricingListCalculation?.pricingList?.name
                        return otherName === pricingListName
                    }).length

                    // Only show distributor name if this name/number appears in 2+ tabs
                    const displayName = duplicateCount >= 2
                        ? `${pricingListName} (${distributorName})`
                        : pricingListName

                    return (
                        <button
                            key={calc.id}
                            className={cn(style.tab, { [style.activeTab]: index === activeTab })}
                            onClick={() => setActiveTab(index)}
                        >
                            {displayName}
                        </button>
                    )
                })}
            </div>

            {/* Tab Content */}
            {activeCalc && (
                <div className={style.tabContent}>
                    <EmailPreview
                        distributorId={activeCalc.distributor.id}
                        pricingListCalculationId={activeCalc.pricingListCalculation.id}
                        virtualTypeNumber={activeCalc.virtualTypeNumber}
                        emails={exportData.distributors.find(d => d.id === activeCalc.distributor.id)?.emails?.page || []}
                    />

                    <div className={style.section}>
                        <div className={style.sectionHeader}>
                            <h3>{t("table:export_content")}</h3>
                            <div className={style.buttonGroup}>
                                <button
                                    className={buttonStyle.button}
                                    onClick={jumpToTop}
                                >
                                    {t("table:jump_to_top")}
                                </button>
                                <button
                                    className={buttonStyle.button}
                                    onClick={jumpToBottom}
                                >
                                    {t("table:jump_to_bottom")}
                                </button>
                                <button
                                    className={buttonStyle.button}
                                    onClick={() => downloadDatFile(activeCalc)}
                                >
                                    {c(t("common:download"))}
                                </button>
                            </div>
                        </div>
                        <textarea
                            ref={textareaRef}
                            className={style.datViewer}
                            value={activeCalc.result || ""}
                            readOnly
                            rows={20}
                        />
                    </div>
                </div>
            )}

            {showConfirmModal && (
                <ConfirmSendModal
                    onConfirm={handleSendEmail}
                    onCancel={() => setShowConfirmModal(false)}
                    distributorNames={exportData.distributors.map(d => d.name)}
                    pricingListCount={calculations.length}
                />
            )}
        </div>
    )
}

const EmailPreview = ({ distributorId, pricingListCalculationId, virtualTypeNumber, emails }) => {
    const { t } = useTranslation(["common", "table"])

    const { data, loading } = useQuery(GET_RENDERED_EMAIL, {
        variables: { distributorId, pricingListCalculationId, virtualTypeNumber },
        skip: !distributorId || !pricingListCalculationId,
    })

    if (loading) {
        return <div className={style.section}>{t("common:loading")}...</div>
    }

    return (
        <>
            <div className={style.section}>
                <h3>{t("table:recipients")}</h3>
                <ul className={style.emailList}>
                    {emails.map((e) => (
                        <li key={e.id}>{e.email}</li>
                    ))}
                </ul>
            </div>

            <div className={style.section}>
                <h3>{t("table:email_subject")}</h3>
                <p className={style.emailContent}>{data?.distributor?.renderedEmailTitle || "-"}</p>
            </div>

            <div className={style.section}>
                <h3>{t("table:email_body")}</h3>
                <div className={style.emailContent} style={{ whiteSpace: "pre-wrap" }}>
                    <Markdown>
                        {data?.distributor?.renderedEmailMessage || "-"}
                    </Markdown>
                </div>
            </div>
        </>
    )
}

const ConfirmSendModal = ({ onConfirm, onCancel, distributorNames, pricingListCount }) => {
    const { t } = useTranslation(["common", "table"])
    const [isConfirmed, setIsConfirmed] = useState(false)

    return (
        <div className={style.modalOverlay} onClick={onCancel}>
            <div className={style.modal} onClick={(e) => e.stopPropagation()}>
                <h2>{t("common:are_you_sure")}</h2>
                <p>
                    {t("table:confirm_send_email_message", {
                        distributor: distributorNames.join(", "),
                        count: pricingListCount
                    })}
                </p>
                <label className={style.checkboxLabel}>
                    <input
                        type="checkbox"
                        checked={isConfirmed}
                        onChange={(e) => setIsConfirmed(e.target.checked)}
                    />
                    {t("table:confirm_send_checkbox")}
                </label>
                <div className={style.modalButtons}>
                    <button
                        className={buttonStyle.button}
                        onClick={onCancel}
                    >
                        {c(t("common:cancel"))}
                    </button>
                    <button
                        className={cn(buttonStyle.button, buttonStyle.primaryButton)}
                        onClick={onConfirm}
                        disabled={!isConfirmed}
                    >
                        {c(t("table:send_email"))}
                    </button>
                </div>
            </div>
        </div>
    )
}
